import { motion } from "framer-motion";
import { Lock, Mail } from "lucide-react";
import { SEO } from "@/components/nyla/SEO";

const sections = [
  {
    title: "1. Who we are",
    paragraphs: [
      "This website, nylaairwater.earth, is operated by Jaitra Wellness Private Limited, the company behind Nyla Air Water (\"Nyla\", \"we\", \"us\"). We are committed to handling personal information responsibly, transparently and in accordance with applicable Indian law, including the Digital Personal Data Protection Act, 2023.",
    ],
  },
  {
    title: "2. Information we collect",
    intro: "We collect only the information needed to respond to you and operate this website:",
    bullets: [
      "Contact details you share through our enquiry form — your name, email address, company name (optional), the nature of your enquiry and your message.",
      "Correspondence you send us by email, including any information you choose to include.",
      "Basic technical information generated when you visit the site, such as pages viewed and approximate request logs, used solely to keep the website secure and functioning.",
    ],
    outro: "We do not ask for, and request that you do not submit, sensitive personal information through this website.",
  },
  {
    title: "3. How we use your information",
    intro: "We use the information you provide to:",
    bullets: [
      "Respond to your enquiries about our products, partnerships, distribution or support.",
      "Communicate with you about a business relationship you have requested or entered into with us.",
      "Maintain the security, performance and reliability of this website.",
      "Comply with legal and regulatory obligations that apply to us.",
    ],
    outro: "We do not use your information for automated decision-making, and we do not send marketing communications unless you have asked us to.",
  },
  {
    title: "4. Cookies and local storage",
    paragraphs: [
      "This website does not use advertising cookies or third-party tracking for marketing purposes. We use only the minimal browser storage necessary for the website to function — for example, secure session storage for authorised administrators of this site. No profile of your browsing behaviour is built or shared.",
    ],
  },
  {
    title: "5. Sharing of information",
    intro: "We do not sell, rent or trade your personal information. We share it only with:",
    bullets: [
      "Service providers who help us operate this website and deliver email communications, under obligations to protect your information.",
      "Professional advisers, regulators or authorities where disclosure is required by law or necessary to protect our legal rights.",
    ],
  },
  {
    title: "6. Data retention",
    paragraphs: [
      "We keep enquiry information only for as long as it is needed to handle your request and any resulting business relationship, or as required by law. Information that is no longer needed is deleted or anonymised.",
    ],
  },
  {
    title: "7. Your rights",
    intro: "Subject to applicable law, you may:",
    bullets: [
      "Request access to the personal information we hold about you.",
      "Request correction of inaccurate or incomplete information.",
      "Request deletion of your information where we no longer need it.",
      "Withdraw consent you have previously given, where processing is based on consent.",
      "Raise a grievance about how your information has been handled.",
    ],
    outro: "To exercise any of these rights, write to us at contactus@nylaairwater.earth. We will respond within a reasonable time and in accordance with applicable law.",
  },
  {
    title: "8. Security",
    paragraphs: [
      "We apply reasonable technical and organisational safeguards to protect personal information against unauthorised access, alteration, disclosure or loss — including access controls, encrypted connections (HTTPS) and restricted administrative access. No method of transmission or storage is completely secure, but we work to protect your information with care.",
    ],
  },
  {
    title: "9. Children",
    paragraphs: [
      "This website is intended for a general audience and is not directed at children. We do not knowingly collect personal information from children. If you believe a child has provided us personal information, please contact us and we will delete it.",
    ],
  },
  {
    title: "10. Changes to this policy",
    paragraphs: [
      "We may update this policy from time to time to reflect changes in our operations or legal requirements. The latest version will always be available on this page, with its effective date shown above. Material changes will be communicated clearly on this website.",
    ],
  },
];

export default function PrivacyPolicy() {
  return (
    <div data-testid="privacy-policy-page" className="pt-14">
      <SEO
        title="Privacy Policy"
        description="How Jaitra Wellness Private Limited (Nyla Air Water) collects, uses and protects your personal information — transparent, minimal and in line with Indian data protection law."
        path="/privacy-policy"
        keywords="Nyla privacy policy, Jaitra Wellness privacy, data protection, DPDP Act"
      />

      <section className="py-20 md:py-28 bg-[#F5F5F7]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <div className="w-12 h-12 rounded-2xl bg-[#2C3E35]/10 flex items-center justify-center mb-6">
              <Lock className="w-6 h-6 text-[#2C3E35]" strokeWidth={1.6} />
            </div>
            <p className="text-xs md:text-sm tracking-[0.2em] uppercase font-semibold text-[#86868B] mb-6">
              Jaitra Wellness Private Limited · Nyla Air Water
            </p>
            <h1 data-testid="privacy-heading" className="font-display text-4xl md:text-5xl lg:text-6xl leading-tight tracking-tight font-bold text-[#1D1D1F]">
              Privacy Policy.
            </h1>
            <div className="mt-8 flex flex-wrap gap-x-8 gap-y-2 text-sm text-[#86868B]">
              <p><span className="font-semibold text-[#1D1D1F]">Effective date:</span> 06 October 2026</p>
              <p><span className="font-semibold text-[#1D1D1F]">Version:</span> 1.0</p>
            </div>
            <p className="mt-8 text-lg leading-relaxed text-[#86868B]">
              We collect very little, we use it only to respond to you, and we never sell it.
              This page explains exactly how your information is handled.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="space-y-12">
            {sections.map((s) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="font-display text-xl md:text-2xl tracking-tight font-bold text-[#1D1D1F] mb-4">{s.title}</h2>
                {s.intro && <p className="text-base leading-relaxed text-[#1D1D1F]/75 mb-4">{s.intro}</p>}
                {s.paragraphs?.map((p, i) => (
                  <p key={i} className="text-base leading-relaxed text-[#1D1D1F]/75 mb-4">{p}</p>
                ))}
                {s.bullets && (
                  <ul className="space-y-2.5 mb-4">
                    {s.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-3 text-base leading-relaxed text-[#1D1D1F]/75">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#2C3E35] mt-2.5 shrink-0" />
                        {b}
                      </li>
                    ))}
                  </ul>
                )}
                {s.outro && <p className="text-sm leading-relaxed text-[#86868B] italic">{s.outro}</p>}
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.6 }}
            className="mt-16 rounded-3xl bg-[#F5F5F7] p-8 md:p-10"
          >
            <h2 className="font-display text-xl md:text-2xl tracking-tight font-bold text-[#1D1D1F] mb-3">Contact</h2>
            <p className="text-base leading-relaxed text-[#1D1D1F]/75 mb-4">
              For privacy questions, requests or grievances, please contact:
            </p>
            <p className="text-base font-medium text-[#1D1D1F]">Jaitra Wellness Private Limited — Nyla Air Water</p>
            <a
              href="mailto:contactus@nylaairwater.earth"
              data-testid="privacy-contact-email"
              className="mt-2 inline-flex items-center gap-2 text-sm font-medium text-[#2C3E35] hover:underline"
            >
              <Mail className="w-4 h-4" /> contactus@nylaairwater.earth
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
